import React from 'react';
import { useIntl } from 'react-intl';

import { Box, IconButton as MuiIconButton, Typography, useTheme } from '@mui/material';
import { Copy, MinusCircle, PlusCircle, Trash2 } from 'lucide-react';
import { useFieldArray, useWatch } from 'react-hook-form';

import Autocomplete from 'components/autocomplete';
import Collapsible from 'components/collapsible';
import IconButton from 'components/icon-button';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import FormListCell from 'components/list/form-list-cell';
import ListRow from 'components/list/list-row';
import NumberField from 'components/number-field';
import TextField from 'components/text-field';
import { isValidInteger } from 'utils/validate-numbers';
import { FeedProgramForm } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-feed-program-form.type';
import { Feed } from 'views/bluviu-nutrition/types/feed.type';

type FeedProgramItemProps = {
  control: any;
  index: number;
  feeds: Feed[];
  onClone: (index: number, currentValues: FeedProgramForm) => void;
  onDelete: () => void;
  canDelete: boolean;
};

const MIN_FISH_SIZE = 50;

const FeedProgramItem: React.FC<FeedProgramItemProps> = ({ control, index, feeds, onClone, onDelete, canDelete }) => {
  const intl = useIntl();
  const theme = useTheme();

  const name = useWatch({
    control,
    name: `feedPrograms.${index}.name`,
  });

  const rows = useWatch({
    control,
    name: `feedPrograms.${index}.rows`,
  });

  const currentProgramValues: FeedProgramForm = {
    name: name || `Feed Program ${index + 1}`,
    rows: rows || [],
  };

  const {
    fields: rowFields,
    append: appendRow,
    remove: removeRow,
  } = useFieldArray({
    control,
    name: `feedPrograms.${index}.rows`,
  });

  return (
    <Collapsible
      open
      hideBorder
      backgroundColor={theme.palette.lightBlue.o25}
      label={
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
          <Typography fontWeight={500}>{currentProgramValues.name}</Typography>
          <Box display={'flex'} gap={1}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClone(index, currentProgramValues);
              }}>
              <Copy size={18} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={!canDelete}>
              <Trash2 size={18} />
            </IconButton>
          </Box>
        </Box>
      }>
      <Box display={'flex'} flexDirection={'column'} gap={3}>
        <Box px={4}>
          <TextField
            dark
            fullWidth
            label="Name"
            controller={{
              control,
              name: `feedPrograms.${index}.name`,
              rules: { required: 'Enter feed program name' },
            }}
          />
        </Box>
        <List
          options={[
            { id: 'startFeedingAtFishSize', label: 'Fish size (g) *', align: 'left' },
            { id: 'feed', label: 'Feed *', align: 'left' },
            { id: 'price', label: 'Customer price', align: 'left' },
            { id: 'actions', label: '', align: 'right' },
          ]}
          dark
          orderBy="feed">
          {rowFields.map((row, rowIndex) => (
            <ListRow key={row.id}>
              <FormListCell align="left">
                <NumberField
                  label="Fish size"
                  hiddenLabel
                  fullWidth
                  dark
                  controller={{
                    control,
                    name: `feedPrograms.${index}.rows.${rowIndex}.startFeedingAtFishSize`,
                    rules: {
                      required: intl.formatMessage({ id: 'form.rules.required' }),
                      validate: {
                        minValue: (value: number) =>
                          isValidInteger(value) && Number(value) >= MIN_FISH_SIZE
                            ? true
                            : intl.formatMessage({ id: 'form.rules.minValue' }, { min: MIN_FISH_SIZE }),
                      },
                    },
                  }}
                />
              </FormListCell>
              <FormListCell align="left">
                <Autocomplete
                  label={'Product'}
                  fullWidth
                  dark
                  disableCloseOnSelect={false}
                  controller={{
                    control,
                    name: `feedPrograms.${index}.rows.${rowIndex}.feed`,
                    rules: { required: 'Select product' },
                  }}
                  options={feeds || []}
                  getOptionLabel={(feed) => feed.name}
                />
              </FormListCell>
              <FormListCell align="left">
                <NumberField
                  label="Customer price"
                  hiddenLabel
                  fullWidth
                  dark
                  controller={{
                    control,
                    name: `feedPrograms.${index}.rows.${rowIndex}.price`,
                    rules: {
                      required: intl.formatMessage({ id: 'form.rules.required' }),
                      validate: (value) => value > 0 || intl.formatMessage({ id: 'form.rules.moreThanZero' }),
                    },
                  }}
                />
              </FormListCell>
              <ListCell>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-around'}>
                  <MuiIconButton
                    size="small"
                    sx={{ color: theme.palette.bluViu.main }}
                    onClick={() => {
                      const currentRowValues = currentProgramValues?.rows?.[rowIndex];
                      if (currentRowValues) {
                        appendRow({ ...currentRowValues });
                      } else {
                        appendRow({ feed: null, startFeedingAtFishSize: 0, price: 0 });
                      }
                    }}>
                    <Copy size={24} />
                  </MuiIconButton>
                  {rowIndex === 0 ? (
                    <MuiIconButton
                      size="small"
                      sx={{ color: theme.palette.bluViu.main }}
                      onClick={() => appendRow({ feed: null, startFeedingAtFishSize: 0, price: 0 })}>
                      <PlusCircle size={24} />
                    </MuiIconButton>
                  ) : (
                    <MuiIconButton size="small" sx={{ color: theme.palette.bluViu.main }} onClick={() => removeRow(rowIndex)}>
                      <MinusCircle size={24} />
                    </MuiIconButton>
                  )}
                </Box>
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Box>
    </Collapsible>
  );
};

export default FeedProgramItem;
